import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Location } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class EventBusService {
  private location = inject(Location);

  readonly GLOBAL_NAMESPACE = 'global';

  private eventBus = {
    global: {}, // used to store state across all pages/ features/ urls etc.
  };

  constructor() {}

  // used to update an existing channel with new data without replacing it completely
  public updateChannel(
    channel: string,
    data: any,
    useNamespace: boolean = true,
  ): void {
    const namespace: string = useNamespace
      ? this.namespace
      : this.GLOBAL_NAMESPACE;

    this.initialiseNamespace(namespace);
    this.initialiseChannel(channel, namespace);

    // clone input in order to avoid mutation further down the line
    const clonedData = structuredClone(data);

    if (this.eventBus[namespace][channel].getValue()) {
      this.eventBus[namespace][channel].next({
        ...this.eventBus[namespace][channel].getValue(),
        ...clonedData,
      });
    } else {
      this.eventBus[namespace][channel].next(clonedData);
    }
  }

  // used to create/ replace a channel with completely new data
  public setChannel(
    channel: string,
    data: any,
    useNamespace: boolean = true,
  ): void {
    const namespace: string = useNamespace
      ? this.namespace
      : this.GLOBAL_NAMESPACE;

    this.initialiseNamespace(namespace);
    this.initialiseChannel(channel, namespace);

    // clone input in order to avoid mutation further down the line
    const clonedData = structuredClone(data);
    this.eventBus[namespace][channel].next(clonedData);
  }

  // used to retrieve a channel's data
  public getChannel(
    channel: string,
    useNamespace: boolean = true,
  ): BehaviorSubject<any> {
    const namespace: string = useNamespace
      ? this.namespace
      : this.GLOBAL_NAMESPACE;

    this.initialiseNamespace(namespace);
    this.initialiseChannel(channel, namespace);

    return this.eventBus[namespace][channel];
  }

  // used to get the current value of a channel synchronously
  public getChannelValue(channel: string, useNamespace: boolean = true): any {
    return this.getChannel(channel, useNamespace)?.getValue() ?? null;
  }

  get namespace(): string {
    // extract component name from route
    // '/account/commercial-invoices' => 'commercial-invoices'
    // '/shared/insights/report-group/54 => insights-report-group-54'
    const path = this.location.path();

    const basePath = path.startsWith('/account/')
      ? path.replace(/^\/account\//, '')
      : path.startsWith('/shared/')
        ? path.replace(/^\/shared\//, '')
        : path;

    return basePath
      .replace(/\//g, '-')
      .replace(/^-+/, '') // Remove leading dashes
      .match(/[\d*|a-z|-]*/)[0];
  }

  private initialiseNamespace(namespace: string): void {
    if (!Object.prototype.hasOwnProperty.call(this.eventBus, namespace)) {
      this.eventBus[namespace] = {};
    }
  }

  private initialiseChannel(channel: string, namespace: string): void {
    if (
      !Object.prototype.hasOwnProperty.call(this.eventBus[namespace], channel)
    ) {
      this.eventBus[namespace][channel] = new BehaviorSubject({});
    }
  }
}
